/**
 * O4 — OverlayRoute: standalone presentational route for the OBS / web
 * spectator overlay (commercial/01-frontend-integration.md §8).
 *
 * Mounts as a sibling-less full-viewport component. Reads a battle
 * JSONL URL + optional transcript URLs from props (typically derived
 * from `parseOverlayParams(window.location.search)`), fetches them,
 * parses, and renders `<MatchOverlay>` over a chromakey / transparent
 * background suitable for an OBS Browser Source.
 *
 * Out of scope (deferred):
 *   - The actual route mounting (Next.js page, React Router route,
 *     Tauri window, …). Consumer decides; this component is a single
 *     full-viewport DOM tree they can drop anywhere.
 *   - Live-match streaming (only post-match VOD replays today; the
 *     transport for "watch this match in-progress" is launch-wiring
 *     §04, not yet in code).
 *   - The 3D replay viewer. O4 renders ONLY the DOM overlay — for a
 *     streamer who wants the full 3D + overlay, mount `<ReplayScene>`
 *     alongside (or have it on a separate Browser Source layer). The
 *     overlay-only mode is what most streamers want (so they can
 *     composite the official viewer feed via OBS source switching).
 *
 * Tick selection: the overlay is post-match-only today, so the user
 * scrubs via consumer-supplied `tick` prop OR via a built-in slider
 * (`controls={true}` renders the playback HUD in the overlay).
 *
 * Loading + error states render explicit DOM (no spinners — the
 * retro-scifi vocabulary doesn't include spinners) so streamers see
 * what failed if a URL is wrong.
 */

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { parseBattle, type BattleFrame, type ParsedBattle } from "@pilot-protocol/replay-parser";
import { MatchOverlay, type OverlayPanel } from "./MatchOverlay";
import type { OverlayTokens } from "./tokens";
import type { TranscriptEntry } from "./transcript";
import type { OverlayBackground } from "./overlayParams";

export interface OverlayRouteProps {
  /** Battle JSONL URL. The route fetches + parses on mount; null
   *  renders the unloaded state. */
  matchUrl: string | null;
  /** Optional per-side transcript JSONL URLs. */
  transcriptUrlA?: string | null;
  transcriptUrlB?: string | null;
  /** Bearer token forwarded to all fetches (Researcher API signed
   *  URL — COM-11). When null, fetches go unauthenticated. */
  authToken?: string | null;
  /** Initial / controlled tick. When `onTickChange` is provided the
   *  parent owns the value; otherwise the route picks tick 0 and
   *  stays there (consumer mounts its own scrubber). */
  tick?: number;
  onTickChange?: (tick: number) => void;
  /** Panels to mount. */
  panels?: ReadonlyArray<OverlayPanel>;
  /** Background mode. */
  background?: OverlayBackground;
  /** Per-instance token overrides. */
  tokens?: Partial<OverlayTokens>;
  /** Show technical reasoning stats. */
  showReasoningStats?: boolean;
  /** Show numeric zone legend. */
  showZoneLegend?: boolean;
  /** Max telemetry rows visible. */
  telemetryMaxRows?: number;
  /** Custom node displayed while the battle is loading. */
  loadingFallback?: ReactNode;
  /** Custom node displayed when a fetch fails. Receives the error. */
  errorFallback?: (error: Error) => ReactNode;
  /** Override `fetch` (tests, mocks). Defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
}

interface LoadState {
  status: "idle" | "loading" | "ready" | "error";
  battle: ParsedBattle | null;
  transcriptA: TranscriptEntry[] | null;
  transcriptB: TranscriptEntry[] | null;
  error: Error | null;
}

const INITIAL_STATE: LoadState = {
  status: "idle",
  battle: null,
  transcriptA: null,
  transcriptB: null,
  error: null,
};

const fullViewportStyle: CSSProperties = {
  position: "relative",
  width: "100vw",
  height: "100vh",
  overflow: "hidden",
};

const messageStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  color: "var(--pp-text-muted, #7a7a7a)",
  textAlign: "center",
  letterSpacing: "var(--pp-tracking-caps)",
  textTransform: "uppercase",
  pointerEvents: "none",
};

export function OverlayRoute({
  matchUrl,
  transcriptUrlA = null,
  transcriptUrlB = null,
  authToken = null,
  tick = 0,
  onTickChange,
  panels,
  background = "normal",
  tokens,
  showReasoningStats,
  showZoneLegend,
  telemetryMaxRows,
  loadingFallback,
  errorFallback,
  fetchImpl,
}: OverlayRouteProps) {
  const [state, setState] = useState<LoadState>(INITIAL_STATE);
  // `onTickChange` is informational here — the route doesn't own a
  // scrubber UI in this lift; the consumer mounts one if needed. We
  // keep it for forward-compat with a future built-in HUD.
  void onTickChange;

  const fetcher = fetchImpl ?? globalThis.fetch?.bind(globalThis);

  useEffect(() => {
    let cancelled = false;
    if (!matchUrl) {
      setState(INITIAL_STATE);
      return;
    }
    if (!fetcher) {
      setState({
        ...INITIAL_STATE,
        status: "error",
        error: new Error("fetch is unavailable in this environment"),
      });
      return;
    }
    setState((s) => ({ ...s, status: "loading", error: null }));
    const headers: Record<string, string> = {};
    if (authToken) headers["Authorization"] = `Bearer ${authToken}`;
    (async () => {
      try {
        const battle = await fetchBattle(matchUrl, fetcher, headers);
        const [transcriptA, transcriptB] = await Promise.all([
          transcriptUrlA
            ? fetchTranscript(transcriptUrlA, fetcher, headers)
            : Promise.resolve<TranscriptEntry[] | null>(null),
          transcriptUrlB
            ? fetchTranscript(transcriptUrlB, fetcher, headers)
            : Promise.resolve<TranscriptEntry[] | null>(null),
        ]);
        if (cancelled) return;
        setState({
          status: "ready",
          battle,
          transcriptA,
          transcriptB,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          ...INITIAL_STATE,
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matchUrl, transcriptUrlA, transcriptUrlB, authToken, fetcher]);

  const overlay = useMemo(() => {
    if (!state.battle) return null;
    return (
      <MatchOverlay
        battle={state.battle}
        tick={tick}
        panels={panels}
        tokens={tokens}
        background={background}
        showZoneLegend={showZoneLegend}
        showReasoningStats={showReasoningStats}
        telemetryMaxRows={telemetryMaxRows}
        transcriptA={state.transcriptA ?? undefined}
        transcriptB={state.transcriptB ?? undefined}
      />
    );
  }, [
    state.battle,
    state.transcriptA,
    state.transcriptB,
    tick,
    panels,
    tokens,
    background,
    showZoneLegend,
    showReasoningStats,
    telemetryMaxRows,
  ]);

  return (
    <div style={fullViewportStyle} data-pp-overlay-route>
      {state.status === "idle" ? (
        <div style={messageStyle}>— no match url —</div>
      ) : null}
      {state.status === "loading" ? (
        <div style={messageStyle}>{loadingFallback ?? "loading replay…"}</div>
      ) : null}
      {state.status === "error" && state.error ? (
        <div style={messageStyle}>
          {errorFallback ? errorFallback(state.error) : `error: ${state.error.message}`}
        </div>
      ) : null}
      {state.status === "ready" ? overlay : null}
    </div>
  );
}

async function fetchBattle(
  url: string,
  fetcher: typeof fetch,
  headers: Record<string, string>,
): Promise<ParsedBattle> {
  const res = await fetcher(url, { headers });
  if (!res.ok) throw new Error(`battle fetch ${res.status} (${url})`);
  const text = await res.text();
  const frames: BattleFrame[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      frames.push(JSON.parse(trimmed) as BattleFrame);
    } catch {
      // Skip malformed lines — bench archives are append-only and a
      // truncated final line is benign for the viewer.
    }
  }
  return parseBattle(frames);
}

async function fetchTranscript(
  url: string,
  fetcher: typeof fetch,
  headers: Record<string, string>,
): Promise<TranscriptEntry[]> {
  const res = await fetcher(url, { headers });
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`transcript fetch ${res.status} (${url})`);
  const text = await res.text();
  const out: TranscriptEntry[] = [];
  for (const line of text.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      out.push(JSON.parse(trimmed) as TranscriptEntry);
    } catch {
      // Same defensive parse as the battle path.
    }
  }
  return out;
}
